import Fetcher from "../Fetcher.ts";
import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import type { FileUpload } from "./FileUpload";
import type { Contact, MailboxTabular, Tabular, VendorRelationship } from "./RFQ";
import type { RFQCommunication, RFQMailbox } from "./RFQMailbox";

export type AwardExportFormat = "csv" | "xlsx" | "pdf";

export type RFQAwardItem = {
    id: number;
    rfqLineItemRowId?: number | null;
    quoteLineItemRowId: number;
    rfqLineItemRow?: Tabular["rows"][number] | null;
    quoteLineItemRow: Tabular["rows"][number];
};

export type RFQAwardPurchaseOrder = {
    id: number;
    poNumber: number | null;
    createdAt: string;
    fileId: number;
    file?: FileUpload;
    communicationId: number;
    communication?: RFQCommunication;
};

export type RFQAward = {
    id: number;
    rationale?: string;
    createdAt: string;
    awardedItems: RFQAwardItem[];
    mailboxTable: MailboxTabular & {
        tabularDataStore: Tabular;
        mailbox: RFQMailbox & {
            vendorRelationship: VendorRelationship;
            // Can't use the RFQMailboxContact type here because it is
            // specific for the Mailboxes use case. The type doesn't match
            // what is returned from the database, but rather returns a
            // computed type.
            mailboxContacts: {
                contact: Contact;
            }[];
        };
    };
    purchaseOrders: RFQAwardPurchaseOrder[];
};

export type CreateAwardParams = {
    mailboxId: number;
    mailboxTableId: number;
    rationale?: string;
    awardItems?: { rfqLineItemRowId?: number | null; quoteLineItemRowId: number }[];
    awardAll?: boolean;
};

const f = Fetcher.default();

export default {
    getRFQAwards: (rfqPublicId: string) => {
        return f.get<RFQAward[]>(`/v2/rfqs/${rfqPublicId}/awards`);
    },

    createAward: (params: CreateAwardParams) => {
        const { mailboxId, mailboxTableId, ...body } = params;
        return f.post<RFQAward>(`/mailboxes/${mailboxId}/tables/${mailboxTableId}/awards`, { body });
    },

    deleteAward: (awardId: number) => {
        return f.delete(`/awards/${awardId}`);
    },

    updateAwardRationale: (awardId: number, rationale: string): Promise<void> => {
        return f.patch(`/awards/${awardId}`, { body: { rationale } });
    },

    /**
     * Export awarded line items as CSV, XLSX, or PDF.
     * Returns a Blob that can be used to trigger a file download.
     */
    exportAwardItems: async (awardId: number, format: AwardExportFormat): Promise<{ blob: Blob; filename: string }> => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/awards/${awardId}/export?format=${format}`, {
            method: "GET",
            headers: getHeaders(),
        });

        if (!response.ok) {
            throw new Error("Failed to export award items");
        }

        // Get filename from Content-Disposition header if available
        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = `award-${awardId}-line-items.${format}`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
            if (filenameMatch?.[1]) {
                filename = filenameMatch[1];
            }
        }

        const blob = await response.blob();
        return { blob, filename };
    },
};
